import { useEffect, useMemo } from "react";
import { useAdminWorkspace } from "../../contexts/AdminWorkspaceProvider";
import { FieldLabel, SelectInput } from "@/components/admin/AdminShell";

type OrganisationPickerProps = {
  id?: string;
  value: number | null;
  onChange: (organisationId: number | null) => void;
  allowEmpty?: boolean;
  emptyLabel?: string;
  label?: string;
  size?: "default" | "compact";
  disabled?: boolean;
};

export function OrganisationPicker({
  id = "organisation-id",
  value,
  onChange,
  allowEmpty = false,
  emptyLabel = "Без организации",
  label = "Организация",
  size = "default",
  disabled = false,
}: OrganisationPickerProps) {
  const { organisations, isLoadingOrganisations: loading } =
    useAdminWorkspace();

  const options = useMemo(() => {
    return organisations.map((org) => ({
      id: org.id,
      label: org.name,
    }));
  }, [organisations]);

  useEffect(() => {
    if (value != null) return;
    if (options.length === 1) {
      onChange(options[0]!.id);
    }
  }, [onChange, options, value]);

  if (loading) {
    return (
      <p className="font-onest text-xs text-slate-500">Загрузка организаций…</p>
    );
  }

  if (options.length === 0) {
    return (
      <p className="font-onest text-xs text-slate-500">
        Нет доступных организаций
      </p>
    );
  }

  if (disabled) {
    const selectedLabel =
      options.find((org) => org.id === value)?.label ??
      (value != null ? `#${value}` : emptyLabel);
    return (
      <div>
        {label ? (
          <FieldLabel htmlFor={id} size={size}>
            {label}
          </FieldLabel>
        ) : null}
        <p className="font-onest text-sm text-slate-700">{selectedLabel}</p>
      </div>
    );
  }

  return (
    <div>
      {label ? (
        <FieldLabel htmlFor={id} size={size}>
          {label}
        </FieldLabel>
      ) : null}
      <SelectInput
        id={id}
        size={size}
        value={value != null ? String(value) : ""}
        onChange={(next) => onChange(next ? Number(next) : null)}
      >
        {allowEmpty ? <option value="">{emptyLabel}</option> : null}
        {options.map((org) => (
          <option key={org.id} value={org.id}>
            {org.label}
          </option>
        ))}
      </SelectInput>
    </div>
  );
}
